import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Check, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { formatInTz } from "@/lib/time";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * NotificationsBell — top-bar bell with unread badge + dropdown.
 *
 * Polls /api/notifications every 30s. On open, refreshes immediately.
 * Click a notification → marks read + navigates to its `link`.
 */
export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const mounted = useRef(true);
  const { currentWorkspace } = useWorkspace();
  // Notification timestamps render in the workspace's configured timezone
  // (falls back to UTC if the workspace hasn't loaded / has no tz set).
  const tz = currentWorkspace?.timezone || "UTC";

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 25 } });
      if (!mounted.current) return;
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch {
      // Silent — bell is a passive surface
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => {
      mounted.current = false;
      clearInterval(t);
    };
  }, [fetchAll]);

  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  const markRead = async (n) => {
    if (n.read) return;
    try {
      await api.post(`/notifications/${n.notification_id}/mark-read`);
      setItems((prev) =>
        prev.map((x) => (x.notification_id === n.notification_id ? { ...x, read: true } : x))
      );
      setUnread((c) => Math.max(0, c - 1));
    } catch {}
  };

  const remove = async (n, e) => {
    e?.stopPropagation();
    try {
      await api.delete(`/notifications/${n.notification_id}`);
      setItems((prev) => prev.filter((x) => x.notification_id !== n.notification_id));
      if (!n.read) setUnread((c) => Math.max(0, c - 1));
    } catch {
      toast.error("Could not dismiss");
    }
  };

  const markAllRead = async () => {
    try {
      const { data } = await api.post("/notifications/mark-all-read");
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
      if (data.marked) toast.success("All caught up");
    } catch {
      toast.error("Could not mark all read");
    }
  };

  const handleClick = (n) => {
    markRead(n);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        className="relative w-10 h-10 rounded-lg hover:bg-[#f5efe6] flex items-center justify-center transition-colors"
        data-testid="notifications-bell"
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
      >
        <Bell className="w-[18px] h-[18px] text-[#4a4a52]" />
        {unread > 0 && (
          <span
            className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-semibold text-white flex items-center justify-center"
            style={{ background: "#dc2626" }}
            data-testid="notifications-unread-count"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notifications-dropdown">
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/5">
          <p className="text-sm font-semibold">Notifications</p>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-[#005e7a] font-medium hover:underline" data-testid="notifications-mark-all-read">
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <Bell className="w-7 h-7 mx-auto text-[#82828c] opacity-50" />
              <p className="text-sm font-medium mt-2">You're all caught up</p>
              <p className="text-xs text-[#82828c] mt-0.5">Notifications about tasks and reminders will show up here.</p>
            </div>
          ) : (
            <ul className="divide-y divide-[#1a1a1f0a]">
              {items.map((n) => (
                <li
                  key={n.notification_id}
                  onClick={() => handleClick(n)}
                  className={`px-4 py-3 cursor-pointer hover:bg-[#f5efe6] transition-colors group ${n.read ? "" : "bg-[#005e7a08]"}`}
                  data-testid={`notification-${n.notification_id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: "#005e7a" }} />}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-tight ${n.read ? "text-[#4a4a52]" : "font-medium text-[#1a1a1f]"}`}>
                        {n.title}
                      </p>
                      {n.body && <p className="text-xs text-[#82828c] mt-0.5 truncate">{n.body}</p>}
                      <p className="text-[10px] text-[#82828c] mt-1.5">{formatInTz(n.created_at, tz)}</p>
                    </div>
                    <button
                      onClick={(e) => remove(n, e)}
                      className="text-[#82828c] hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`notification-delete-${n.notification_id}`}
                      aria-label="Dismiss"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
